import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, FileText, Mail, Pencil, ChevronLeft, ChevronDown, ListChecks, Clock, MapPin, Users, UserCheck, CheckCircle2, CircleAlert } from "lucide-react";
import api from "../lib/api";
import { exportRiunione, exportSegnalazioniReport } from "../lib/exporter";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const meseLabel = (m) => { try { return capFirst(new Date(m + "-01T00:00:00").toLocaleDateString("it-IT", { month: "long", year: "numeric" })); } catch { return m; } };
const fmtGiornoLong = (iso) => { try { return capFirst(new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })); } catch { return iso; } };
const last12Months = () => { const a = []; const d = new Date(); d.setDate(1); for (let i = 0; i < 12; i++) { a.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); } return a; };
const DOW_ABBR = ["", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const fullName = (o) => `${o.cognome || ""} ${o.nome || ""}`.trim();

// Card di una singola riunione nel dettaglio giorno. Tiene stato locale per presenze e decisioni.
function RiunioneCard({ r, operatori, onEdit, onPdf, onInvia, onDel, patch, strutturaNome, companyLogo }) {
  const { activeOperatore, opName } = useFirma();
  const { user } = useAuth();
  const [lista, setLista] = useState([]);
  const [punti, setPunti] = useState([]);
  const [repBusy, setRepBusy] = useState(false);
  const [open, setOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 640 : true));
  const decisore = activeOperatore ? opName : ((user?.role === "admin" || user?.role === "super_admin") ? "Admin" : (user?.name || "Admin"));

  const scaricaReportSegn = async () => {
    const mese = (r.data || "").slice(0, 7);
    setRepBusy(true);
    try {
      const { data } = await api.get("/segnalazioni/report", { params: { structure_id: r.structure_id, mese } });
      if (!data.totale) { toast.info("Nessuna segnalazione in questo mese"); return; }
      exportSegnalazioniReport({ mese, strutturaNome, perCategoria: data.per_categoria || {}, logo: companyLogo });
      toast.success("Report segnalazioni allegato (PDF)");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore report"); }
    finally { setRepBusy(false); }
  };

  useEffect(() => {
    // Se non c'è ancora una lista partecipanti salvata, prova a ricostruirla dagli id se presenti
    const base = Array.isArray(r.partecipanti_lista) ? r.partecipanti_lista : [];
    setLista(base.map((p) => ({ ...p })));
    setPunti((r.punti || []).map((p) => ({ ...p })));
  }, [r.id, r.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const presenti = lista.filter((p) => p.presente).length;

  const togglePresente = (idx) => {
    const next = lista.map((p, i) => (i === idx ? { ...p, presente: !p.presente } : p));
    setLista(next);
    patch(r, { partecipanti_lista: next });
  };
  const saveDecisione = (idx, val) => {
    if ((punti[idx]?.decisione || "") === (val || "")) return;
    const next = punti.map((p, i) => (i === idx ? { ...p, decisione: val, decisione_da: val.trim() ? decisore : "", decisione_il: val.trim() ? new Date().toISOString() : "" } : p));
    setPunti(next);
    patch(r, { punti: next });
  };

  return (
    <div className="gr-card p-5" data-testid={`riu-card-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <button type="button" onClick={() => setOpen((v) => !v)} className="min-w-0 flex-1 text-left flex items-start gap-2 cursor-pointer" data-testid={`riu-card-toggle-${r.id}`} aria-expanded={open}>
          <ChevronDown size={20} className={`shrink-0 mt-1 text-teal-700 transition-transform ${open ? "" : "-rotate-90"}`} />
          <div className="min-w-0">
            <h2 className="font-head font-bold text-lg">{r.titolo}</h2>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
              {r.ora && <span className="inline-flex items-center gap-1"><Clock size={13} /> {r.ora}</span>}
              {r.luogo && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {r.luogo}</span>}
              {r.organizzatore && <span className="inline-flex items-center gap-1"><Users size={13} /> {r.organizzatore}</span>}
              {r.inviata_il && <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Mail size={13} /> inviata a {r.inviata_a}</span>}
              {!open && <span className="inline-flex items-center gap-1 text-teal-700 font-semibold"><UserCheck size={13} /> {presenti}/{lista.length} presenti · {punti.length} punti</span>}
            </div>
          </div>
        </button>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`riu-edit-${r.id}`}><Pencil size={14} className="mr-1" /> Modifica</Button>
          <Button size="sm" variant="outline" onClick={() => onPdf(r)} data-testid={`riu-pdf-${r.id}`}><FileText size={14} className="mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={scaricaReportSegn} disabled={repBusy} title="Allega il PDF delle segnalazioni del mese della riunione" className="text-amber-700 border-amber-300" data-testid={`riu-report-segn-${r.id}`}><CircleAlert size={14} className="mr-1" /> Report segnalazioni</Button>
          <Button size="sm" onClick={() => onInvia(r)} data-testid={`riu-invia-${r.id}`} className="bg-teal-600 hover:bg-teal-700"><Mail size={14} className="mr-1" /> Invia a tutti</Button>
          <button onClick={() => onDel(r)} className="text-muted-foreground hover:text-red-600 p-2" data-testid={`riu-del-${r.id}`}><Trash2 size={15} /></button>
        </div>
      </div>

      {open && (<>
      {/* Presenze */}
      <div className="mt-4 border-t pt-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
          <UserCheck size={13} /> Presenze <span className="text-teal-700">· presenti {presenti}/{lista.length}</span>
          <span className="font-normal normal-case text-muted-foreground/80">— spunta chi è presente</span>
        </div>
        {lista.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nessun partecipante selezionato. Usa «Modifica» per aggiungerli.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {lista.map((p, i) => (
              <label key={i} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 cursor-pointer transition-colors ${p.presente ? "bg-emerald-50 border-emerald-200" : "bg-secondary/40"}`} data-testid={`riu-presenza-${r.id}-${i}`}>
                <Checkbox checked={!!p.presente} onCheckedChange={() => togglePresente(i)} data-testid={`riu-presenza-cb-${r.id}-${i}`} />
                <span className={`text-sm truncate ${p.presente ? "font-semibold text-emerald-800" : "text-muted-foreground line-through"}`}>{p.nome}</span>
              </label>
            ))}
          </div>
        )}
        {(r.partecipanti_extra || "").trim() && <div className="text-xs text-muted-foreground mt-2"><b>Altri:</b> {r.partecipanti_extra}</div>}
      </div>

      {/* Punti + decisioni */}
      <div className="mt-4 border-t pt-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><ListChecks size={13} /> Punti all'ordine del giorno</div>
        <div className="space-y-3">
          {punti.length === 0 && <p className="text-sm text-muted-foreground italic">Nessun punto inserito.</p>}
          {punti.map((p, i) => (
            <div key={i} className="flex gap-3" data-testid={`riu-punto-${r.id}-${i}`}>
              <span className="w-6 h-6 rounded-full bg-teal-50 text-teal-700 font-bold grid place-items-center text-xs shrink-0 mt-0.5">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm">{p.testo}</div>
                {(p.note || "").trim() && <div className="text-xs text-muted-foreground mt-0.5"><b>Note:</b> {p.note}</div>}
                <div className="mt-1.5">
                  <Label className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Decisione presa</Label>
                  <Textarea
                    defaultValue={p.decisione || ""}
                    onBlur={(e) => saveDecisione(i, e.target.value)}
                    placeholder="Scrivi qui la decisione presa su questo punto…"
                    rows={2}
                    className="text-sm mt-0.5 bg-emerald-50/40 border-emerald-100 focus-visible:ring-emerald-300"
                    data-testid={`riu-decisione-${r.id}-${i}`}
                  />
                  {(p.decisione || "").trim() && (
                    <div className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1">
                      <CheckCircle2 size={11} /> Salvato automaticamente{p.decisione_da ? ` · scritto da ${p.decisione_da}` : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        {r.note && <div className="mt-3 text-sm rounded-lg bg-amber-50 border border-amber-200 p-2.5"><b>Note:</b> {r.note}</div>}
      </div>
      </>)}
    </div>
  );
}

export default function Riunioni() {
  const qc = useQueryClient();
  const { selectedStructure, structures = [], companyLogo } = useAuth();
  const { activeOperatore } = useFirma();
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [openDay, setOpenDay] = useState(null);
  const [edit, setEdit] = useState(null);
  const noStruct = !selectedStructure || selectedStructure === "all";
  const sid = noStruct ? null : selectedStructure;
  const todayIso = ymd(new Date());
  const strutturaNome = (structures.find((s) => s.id === selectedStructure) || {}).nome || "";

  const { data: mData } = useQuery({
    enabled: !noStruct,
    queryKey: ["riunioni-mese", sid, mese],
    queryFn: async () => (await api.get("/riunioni/mese", { params: { structure_id: sid, mese } })).data,
  });
  const { data: dList = [] } = useQuery({
    enabled: !!openDay && !noStruct,
    queryKey: ["riunioni-day", sid, openDay],
    queryFn: async () => (await api.get("/riunioni", { params: { structure_id: sid, data: openDay } })).data,
  });
  const { data: operatori = [] } = useQuery({
    enabled: !noStruct,
    queryKey: ["riunioni-operatori", sid],
    queryFn: async () => (await api.get("/firma/operatori", { params: { structure_id: sid } })).data,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["riunioni-mese"] }); qc.invalidateQueries({ queryKey: ["riunioni-day"] }); };
  const tot = mData?.totali || {};

  const newRiunione = () => setEdit({
    structure_id: sid, data: openDay, ora: "", luogo: "", titolo: "",
    organizzatore: activeOperatore ? `${activeOperatore.nome} ${activeOperatore.cognome}`.trim() : "",
    partecipanti_lista: [], partecipanti_extra: "", note: "", punti: [{ testo: "", note: "", decisione: "" }],
  });

  const openEdit = (r) => setEdit({
    ...r,
    partecipanti_lista: Array.isArray(r.partecipanti_lista) ? r.partecipanti_lista.map((p) => ({ ...p })) : [],
    partecipanti_extra: r.partecipanti_extra || r.partecipanti || "",
    punti: (r.punti && r.punti.length ? r.punti.map((p) => ({ testo: p.testo || "", note: p.note || "", decisione: p.decisione || "" })) : [{ testo: "", note: "", decisione: "" }]),
  });

  const toggleOperatore = (o) => {
    const list = edit.partecipanti_lista || [];
    const exists = list.find((p) => p.id === o.id);
    const next = exists ? list.filter((p) => p.id !== o.id) : [...list, { id: o.id, nome: fullName(o), presente: false }];
    setEdit({ ...edit, partecipanti_lista: next });
  };

  const save = async () => {
    if (!edit.titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    const punti = (edit.punti || []).filter((p) => (p.testo || "").trim());
    const body = { ...edit, punti };
    try {
      if (edit.id) await api.put(`/riunioni/${edit.id}`, body);
      else await api.post("/riunioni", body);
      toast.success("Riunione salvata"); setEdit(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // Salvataggio parziale (presenze/decisioni) dal dettaglio, senza aprire il form
  const patch = async (r, changes) => {
    try {
      await api.put(`/riunioni/${r.id}`, changes);
      qc.invalidateQueries({ queryKey: ["riunioni-day"] });
      qc.invalidateQueries({ queryKey: ["riunioni-mese"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const del = async (r) => { if (!window.confirm("Eliminare questa riunione?")) return; await api.delete(`/riunioni/${r.id}`); toast.success("Eliminata"); refresh(); };
  const invia = async (r) => {
    if (!window.confirm("Inviare il verbale via email a tutti gli operatori della struttura?")) return;
    try { const res = await api.post(`/riunioni/${r.id}/invia`); toast.success(`Verbale inviato a ${res.data.inviata_a} operatori`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); }
  };
  const pdf = (r) => {
    try { exportRiunione({ riunione: r, strutturaNome, color: "#0f766e", logo: companyLogo }); toast.success("PDF generato"); }
    catch (e) { toast.error("Errore nella generazione del PDF"); }
  };

  const selectedIds = new Set((edit?.partecipanti_lista || []).map((p) => p.id));

  if (noStruct) return <div className="p-8"><h1 className="text-2xl font-head font-bold mb-2">Riunioni</h1><p className="text-muted-foreground">Seleziona una struttura dal menu in alto per gestire le riunioni.</p></div>;

  return (
    <div className="p-4 sm:p-6 lg:px-10" data-testid="riunioni-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-teal-100 grid place-items-center"><CalendarDays className="text-teal-700" size={22} /></div>
        <div><h1 className="text-2xl font-head font-bold">Riunioni</h1><p className="text-sm text-muted-foreground">{strutturaNome} · organizza riunioni, verbalizza le decisioni e invia tutto via email</p></div>
      </div>

      {/* Box riepilogativi */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="gr-card p-4" data-testid="riu-kpi-tot"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Riunioni mese</div><div className="text-3xl font-head font-bold text-teal-700">{tot.riunioni || 0}</div></div>
        <div className="gr-card p-4" data-testid="riu-kpi-prossime"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Da oggi in poi</div><div className="text-3xl font-head font-bold text-sky-700">{tot.prossime || 0}</div></div>
        <div className="gr-card p-4" data-testid="riu-kpi-punti"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Punti discussi</div><div className="text-3xl font-head font-bold">{tot.punti || 0}</div></div>
        <div className="gr-card p-4" data-testid="riu-kpi-decisioni"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Decisioni prese</div><div className="text-3xl font-head font-bold text-emerald-700">{tot.decisioni || 0}</div></div>
      </div>

      {!openDay ? (
        <>
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
            {last12Months().map((m) => (
              <button key={m} onClick={() => setMese(m)} data-testid={`riu-mese-${m}`} className={`px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 border transition-colors capitalize ${mese === m ? "bg-teal-600 text-white border-teal-600" : "bg-white hover:bg-secondary"}`}>{meseLabel(m)}</button>
            ))}
          </div>
          <h3 className="font-head font-bold text-base mb-3">Giorni di <span className="capitalize">{meseLabel(mese)}</span> <span className="text-xs font-normal text-muted-foreground">— clicca un giorno per vedere o creare una riunione</span></h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="riu-day-grid">
            {(mData?.giorni || []).map((g) => (
              <button key={g.data} onClick={() => setOpenDay(g.data)} data-testid={`riu-day-${g.data}`}
                className={`text-left bg-white rounded-xl border p-3 transition-shadow hover:shadow-md cursor-pointer ${g.data === todayIso ? "ring-2 ring-teal-400" : ""} ${g.count ? "border-teal-200" : ""}`}>
                <div className="flex items-baseline justify-between"><div className="font-head font-bold text-lg leading-none">{g.giorno}<span className="text-[10px] font-semibold text-muted-foreground ml-1">{DOW_ABBR[g.dow]}</span></div>{g.count > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700">{g.count}</span>}</div>
                {g.count === 0 ? <div className="text-[11px] text-muted-foreground mt-2">—</div> : (
                  <div className="mt-2 space-y-0.5">{g.titoli.map((t, i) => <div key={i} className="text-[11px] font-medium truncate text-teal-800">• {t}</div>)}</div>
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div data-testid="riu-detail">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Button variant="outline" size="sm" onClick={() => setOpenDay(null)} data-testid="riu-back"><ChevronLeft size={15} className="mr-1" /> Torna al mese</Button>
            <h3 className="font-head font-bold text-base">{fmtGiornoLong(openDay)}</h3>
            <Button size="sm" className="ml-auto" onClick={newRiunione} data-testid="riu-new"><Plus size={15} className="mr-1" /> Nuova riunione</Button>
          </div>
          {dList.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground gr-card"><CalendarDays className="mx-auto mb-3 opacity-40" size={40} /><p>Nessuna riunione in questo giorno.</p><p className="text-sm">Clicca "Nuova riunione" per organizzarne una.</p></div>
          ) : (
            <div className="space-y-4">
              {dList.map((r) => (
                <RiunioneCard key={r.id} r={r} operatori={operatori} onEdit={openEdit} onPdf={pdf} onInvia={invia} onDel={del} patch={patch} strutturaNome={strutturaNome} companyLogo={companyLogo} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="riu-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica riunione" : "Nuova riunione"}{edit?.data ? ` · ${fmtGiornoLong(edit.data)}` : ""}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label className="text-xs font-semibold">Titolo *</Label><Input value={edit.titolo} onChange={(e) => setEdit({ ...edit, titolo: e.target.value })} placeholder="Es. Riunione mensile équipe" data-testid="riu-f-titolo" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Ora</Label><Input type="time" value={edit.ora} onChange={(e) => setEdit({ ...edit, ora: e.target.value })} data-testid="riu-f-ora" /></div>
                <div><Label className="text-xs font-semibold">Luogo</Label><Input value={edit.luogo} onChange={(e) => setEdit({ ...edit, luogo: e.target.value })} placeholder="Es. Sala riunioni" data-testid="riu-f-luogo" /></div>
              </div>
              <div><Label className="text-xs font-semibold">Organizzatore</Label><Input value={edit.organizzatore} onChange={(e) => setEdit({ ...edit, organizzatore: e.target.value })} data-testid="riu-f-org" /></div>

              {/* Partecipanti tra gli operatori */}
              <div>
                <Label className="text-xs font-semibold flex items-center gap-1.5"><Users size={13} /> Partecipanti <span className="text-muted-foreground font-normal">— seleziona gli operatori in riunione ({(edit.partecipanti_lista || []).length})</span></Label>
                <div className="mt-1.5 max-h-48 overflow-y-auto rounded-lg border p-2 grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="riu-f-operatori">
                  {operatori.length === 0 && <p className="text-xs text-muted-foreground p-1">Nessun operatore disponibile.</p>}
                  {operatori.map((o) => {
                    const checked = selectedIds.has(o.id);
                    return (
                      <label key={o.id} className={`flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors ${checked ? "bg-teal-50" : "hover:bg-secondary"}`} data-testid={`riu-f-op-${o.id}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleOperatore(o)} data-testid={`riu-f-op-cb-${o.id}`} />
                        <span className="text-sm truncate">{fullName(o)}{o.qualifica ? <span className="text-muted-foreground"> · {o.qualifica}</span> : null}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div><Label className="text-xs font-semibold">Altri partecipanti <span className="text-muted-foreground font-normal">(persone esterne, separate da virgola)</span></Label><Input value={edit.partecipanti_extra} onChange={(e) => setEdit({ ...edit, partecipanti_extra: e.target.value })} placeholder="Es. Dott. Rossi, familiare Sig.ra Bianchi" data-testid="riu-f-part-extra" /></div>

              {/* Punti: testo + note (nessuna decisione qui) */}
              <div>
                <div className="flex items-center justify-between mb-1"><Label className="text-xs font-semibold">Punti all'ordine del giorno</Label>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => setEdit({ ...edit, punti: [...(edit.punti || []), { testo: "", note: "", decisione: "" }] })} data-testid="riu-f-add-punto"><Plus size={13} className="mr-1" /> Punto</Button>
                </div>
                <div className="space-y-2">
                  {(edit.punti || []).map((p, i) => (
                    <div key={i} className="rounded-lg border p-2.5 bg-secondary/30" data-testid={`riu-f-punto-${i}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-700 font-bold grid place-items-center text-[11px] shrink-0">{i + 1}</span>
                        <Input value={p.testo} onChange={(e) => { const punti = [...edit.punti]; punti[i] = { ...p, testo: e.target.value }; setEdit({ ...edit, punti }); }} placeholder="Argomento / punto da discutere" className="h-8" data-testid={`riu-f-punto-testo-${i}`} />
                        <button onClick={() => setEdit({ ...edit, punti: edit.punti.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-red-600 p-1 shrink-0" data-testid={`riu-f-punto-del-${i}`}><Trash2 size={14} /></button>
                      </div>
                      <Textarea value={p.note} onChange={(e) => { const punti = [...edit.punti]; punti[i] = { ...p, note: e.target.value }; setEdit({ ...edit, punti }); }} placeholder="Note sul punto da discutere (facoltative)" rows={2} className="text-sm" data-testid={`riu-f-punto-note-${i}`} />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">Le decisioni prese si aggiungono dopo, dal dettaglio della riunione.</p>
              </div>
              <div><Label className="text-xs font-semibold">Note generali</Label><Textarea value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} rows={2} placeholder="Note libere" data-testid="riu-f-note" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button><Button onClick={save} className="bg-teal-600 hover:bg-teal-700" data-testid="riu-f-save">Salva riunione</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
